import { images } from 'src/images';
import {
  IAppDraft,
  IBase,
  IClientConfigDraft,
  IClientDataDraft,
  IClientInfoDraft,
  IDetailFormField,
  IDetailFormFieldDefinition,
  IIdentityProviderDraft,
  ISaeIntegrationDraft,
  ISelectElement,
} from '../models';
import { ClientType, IdentityProviderType, InputType, SaeType } from '../models/enums';
import { ToggleComponent } from '../shared/input/toggle/toggle.component';
import { APP } from '../config/app';

export function buildDetailFormField(definition: IDetailFormFieldDefinition, edit: boolean): IDetailFormField {
  const isToggle = definition.inputType === InputType.TOGGLE;
  const isMultiselect = definition.inputType === InputType.MULTISELECT;
  const isSelect2 = definition.inputType === InputType.SELECT_SEARCH;
  return {
    img: definition.img,
    title: definition.title,
    value: edit ? (definition.editValue ?? definition.displayValue) : definition.displayValue,
    required: !!definition.required,
    color: true,
    component: !edit && isToggle ? ToggleComponent : undefined,
    array: !edit && isMultiselect,
    translateValue: definition.translateValue,
    loadOptions: definition.loadOptions,
    edit: {
      id: definition.valueToChange,
      inputType: definition.inputType,
      required: !!definition.required,
      values: isSelect2 ? toSelect2Options(definition.values) : definition.values,
      selectedValues: definition.selectedValues ? [...definition.selectedValues] : undefined,
      valueToChange: definition.valueToChange,
      translateValue: definition.translateValue,
      disabled: definition.disabled,
      placeholder: definition.placeholder,
      emitOnChange: definition.emitOnChange,
      customSearchEnabled: !!definition.loadOptions,
    } as IDetailFormField['edit'],
  };
}

function toSelect2Options(values?: unknown[]) {
  return (values as ISelectElement[] | undefined)?.map((element) => ({ value: element.id, label: element.name }));
}

export function selectOptions(values: readonly string[], literalPrefix: string): ISelectElement[] {
  return values.map((value) => ({ id: value, name: `${literalPrefix}${value}` }));
}

function selectedElements(elements: ISelectElement[], selectedIds: unknown[]): ISelectElement[] {
  const selected = new Set(selectedIds);
  return elements.filter((element) => selected.has(element.id));
}

export function getClientDataForm(
  draft: IClientDataDraft,
  defaultTown: IBase | null,
  edit: boolean,
  loadTowns: (searchValue: string) => Promise<IBase[]>,
): IDetailFormField[] {
  const clientTypes = selectOptions(Object.values(ClientType), 'clients.clientTypes.');
  return [
    { title: 'clients.data.name', img: images.sidebar.clients, valueToChange: 'name', inputType: InputType.TEXT,
      displayValue: draft.name, required: true },
    { title: 'clients.data.code', img: images.login.code, valueToChange: 'code', inputType: InputType.TEXT,
      displayValue: draft.code, required: true },
    { title: 'clients.data.taxNumber', img: images.services.tag, valueToChange: 'taxNumber',
      inputType: InputType.TEXT, displayValue: draft.taxNumber },
    { title: 'clients.data.clientType', img: images.services.tag, valueToChange: 'clientType',
      inputType: InputType.SELECT_SEARCH,
      displayValue: draft.clientType ? `clients.clientTypes.${draft.clientType}` : '',
      editValue: draft.clientType, values: clientTypes, translateValue: true, required: true },
    { title: 'clients.data.address', img: images.user.settlement, valueToChange: 'address',
      inputType: InputType.LONG_TEXT, displayValue: draft.address },
    { title: 'clients.data.defaultTown', img: images.area, valueToChange: 'defaultTownId',
      inputType: InputType.INFINITE_SCROLL, displayValue: defaultTown?.name ?? '', editValue: draft.defaultTownId,
      values: defaultTown ? [{ value: defaultTown.id, label: defaultTown.name }] : [], required: true,
      loadOptions: loadTowns },
  ].map((definition) => buildDetailFormField(definition, edit));
}

export function getClientConfigForm(draft: IClientConfigDraft, edit: boolean): IDetailFormField[] {
  return [
    { title: 'clients.config.maxFaults', img: images.alert, valueToChange: 'maxFaults', inputType: InputType.NUMBER,
      displayValue: draft.maxFaults, required: true },
    { title: 'clients.config.faultWeeksDuration', img: images.user.calendar, valueToChange: 'faultWeeksDuration',
      inputType: InputType.NUMBER, displayValue: draft.faultWeeksDuration, required: true },
    { title: 'clients.config.penaltyWeeksDuration', img: images.user.calendar, valueToChange: 'penaltyWeeksDuration',
      inputType: InputType.NUMBER, displayValue: draft.penaltyWeeksDuration, required: true },
    { title: 'clients.config.maxKmsPerMonth', img: images.distance, valueToChange: 'maxKmsPerMonth',
      inputType: InputType.NUMBER, displayValue: draft.maxKmsPerMonth },
    { title: 'clients.config.phoneRequired', img: images.user.phone, valueToChange: 'phoneRequired',
      inputType: InputType.TOGGLE, displayValue: draft.phoneRequired },
    { title: 'clients.config.planningSync', img: images.vehicles.settings, valueToChange: 'planningSync',
      inputType: InputType.TOGGLE, displayValue: draft.planningSync },
  ].map((definition) => buildDetailFormField(definition, edit));
}

export function getClientInfoForm(
  draft: IClientInfoDraft,
  edit: boolean,
  towns: ISelectElement[] | null,
): IDetailFormField[] {
  const urlField = (title: string, valueToChange: keyof IClientInfoDraft): IDetailFormFieldDefinition => ({
    title, img: images.link, valueToChange, inputType: InputType.LONG_TEXT, displayValue: draft[valueToChange] ?? '',
  });
  const toggleField = (title: string, valueToChange: keyof IClientInfoDraft): IDetailFormFieldDefinition => ({
    title, img: images.vehicles.settings, valueToChange, inputType: InputType.TOGGLE,
    displayValue: !!draft[valueToChange],
  });
  const townField: IDetailFormFieldDefinition[] = towns
    ? [{ title: 'clients.info.areasLabel', img: images.area, valueToChange: 'townIds', inputType: InputType.MULTISELECT,
        displayValue: selectedElements(towns, draft.townIds), values: towns,
        selectedValues: selectedElements(towns, draft.townIds), required: true }]
    : [];
  return [
    ...townField,
    urlField('clients.info.url', 'url'),
    { title: 'clients.info.email', img: images.user.mail, valueToChange: 'email', inputType: InputType.EMAIL,
      displayValue: draft.email ?? '' },
    { title: 'clients.info.phoneNumber', img: images.user.phone, valueToChange: 'phoneNumber',
      inputType: InputType.NUMBER, displayValue: draft.phoneNumber ?? '' },
    { title: 'clients.info.phoneNumberDescription', img: images.user.phone, valueToChange: 'phoneNumberDescription',
      inputType: InputType.LONG_TEXT, displayValue: draft.phoneNumberDescription ?? '' },
    urlField('clients.info.appUrl', 'appUrl'),
    { title: 'clients.info.appEmail', img: images.user.mail, valueToChange: 'appEmail', inputType: InputType.EMAIL,
      displayValue: draft.appEmail ?? '' },
    urlField('clients.info.logoUrl', 'logoUrl'),
    urlField('clients.info.mapLogoUrl', 'mapLogoUrl'),
    urlField('clients.info.privacyPolicyUrl', 'privacyPolicyUrl'),
    urlField('clients.info.termsUrl', 'termsUrl'),
    toggleField('clients.info.logoOnReservations', 'logoOnReservations'),
    toggleField('clients.info.logoOnMap', 'logoOnMap'),
  ].map((definition) => buildDetailFormField(definition, edit));
}

export function getAppForm(
  draft: IAppDraft,
  edit: boolean,
  identityProviders: ISelectElement[],
  clients: ISelectElement[],
): IDetailFormField[] {
  const identityProvider = identityProviders.find((option) => option.id === draft.identityProviderId);
  const toggleField = (title: string, valueToChange: keyof IAppDraft): IDetailFormFieldDefinition => ({
    title, img: images.vehicles.settings, valueToChange, inputType: InputType.TOGGLE,
    displayValue: !!draft[valueToChange],
  });
  return [
    { title: 'integrations.apps.data.name', img: images.sidebar.apps, valueToChange: 'name',
      inputType: InputType.TEXT, displayValue: draft.name, required: true },
    { title: 'integrations.apps.data.code', img: images.login.code, valueToChange: 'code',
      inputType: InputType.TEXT, displayValue: draft.code, required: true },
    { title: 'integrations.apps.data.identityProvider', img: images.sidebar.identityProvider,
      valueToChange: 'identityProviderId',
      inputType: InputType.SELECT_SEARCH, displayValue: identityProvider?.name ?? '',
      editValue: draft.identityProviderId,
      values: identityProviders, required: true },
    { title: 'integrations.apps.data.acceptedIdentityProviders', img: images.sidebar.identityProvider,
      valueToChange: 'acceptedIdentityProviderIds', inputType: InputType.MULTISELECT,
      displayValue: selectedElements(identityProviders, draft.acceptedIdentityProviderIds), values: identityProviders,
      selectedValues: selectedElements(identityProviders, draft.acceptedIdentityProviderIds), emitOnChange: true },
    { title: 'integrations.apps.data.clients', img: images.sidebar.clients, valueToChange: 'clientIds',
      inputType: InputType.MULTISELECT, displayValue: selectedElements(clients, draft.clientIds), values: clients,
      selectedValues: selectedElements(clients, draft.clientIds) },
    toggleField('integrations.apps.data.maintenance', 'maintenance'),
    toggleField('integrations.apps.data.userValidation', 'userValidation'),
    toggleField('integrations.apps.data.userDeleteValidation', 'userDeleteValidation'),
  ].map((definition) => buildDetailFormField(definition, edit));
}

export function withMainIdentityProviderAccepted(draft: IAppDraft): IAppDraft {
  const mainIdentityProviderId = Number(draft.identityProviderId);
  if (!mainIdentityProviderId || draft.acceptedIdentityProviderIds.map(Number).includes(mainIdentityProviderId)) {
    return draft;
  }
  return { ...draft, acceptedIdentityProviderIds: [...draft.acceptedIdentityProviderIds, mainIdentityProviderId] };
}

export function getIdentityProviderForm(
  draft: IIdentityProviderDraft,
  edit: boolean,
  existing: boolean,
  configuredSecrets: ReadonlySet<string>,
  translate: (literalKey: string) => string,
): IDetailFormField[] {
  const fieldLabelPrefix = 'integrations.identityProviders.fields.';
  const fieldOptions = selectOptions(APP.IDENTITY_PROVIDER_FIELDS_TO_SAVE, fieldLabelPrefix);
  const textField = (
    valueToChange: keyof IIdentityProviderDraft,
    img: string,
    inputType = InputType.TEXT,
    required = true,
  ): IDetailFormFieldDefinition => ({
    title: `integrations.identityProviders.configuration.${valueToChange}`, img,
    valueToChange, inputType, displayValue: draft[valueToChange], required,
  });
  const secretField = (valueToChange: 'accessKey' | 'secretKey'): IDetailFormFieldDefinition => {
    const configured = configuredSecrets.has(valueToChange);
    return {
      title: `integrations.identityProviders.configuration.${valueToChange}`, img: images.user.password,
      valueToChange, inputType: InputType.TEXT, displayValue: configured ? 'superadminZone.secretConfigured' : '',
      editValue: draft[valueToChange], translateValue: configured, required: !configured,
      placeholder: configured ? translate('superadminZone.secretKeepPlaceholder') : '',
    };
  };
  const userFields: IDetailFormFieldDefinition[] = draft.provider && draft.provider !== IdentityProviderType.ECORRIDOR
    ? [
        { title: 'integrations.identityProviders.data.uniqueIdType', img: images.login.code,
          valueToChange: 'uniqueIdType', inputType: InputType.SELECT_SEARCH,
          displayValue: draft.uniqueIdType ? `${fieldLabelPrefix}${draft.uniqueIdType}` : '',
          editValue: draft.uniqueIdType, translateValue: true, required: true,
          values: selectOptions(APP.IDENTITY_PROVIDER_UNIQUE_ID_TYPES, fieldLabelPrefix) },
        { title: 'integrations.identityProviders.data.fieldsToSave', img: images.user.account,
          valueToChange: 'fieldsToSave', inputType: InputType.MULTISELECT, translateValue: true,
          displayValue: selectedElements(fieldOptions, draft.fieldsToSave), values: fieldOptions,
          selectedValues: selectedElements(fieldOptions, draft.fieldsToSave) },
      ]
    : [];
  const configurationFields: Partial<Record<IdentityProviderType, IDetailFormFieldDefinition[]>> = {
    [IdentityProviderType.AWS_COGNITO]: [
      textField('region', images.vehicles.settings), textField('userPoolId', images.vehicles.settings),
      secretField('accessKey'), secretField('secretKey'),
    ],
    [IdentityProviderType.ECORRIDOR]: [textField('electronicWalletUrl', images.link, InputType.LONG_TEXT)],
    [IdentityProviderType.KEYCLOAK]: [
      textField('serverUrl', images.link, InputType.LONG_TEXT),
      textField('realm', images.services.tag),
      textField('clientId', images.services.tag),
      textField('syncClaim', images.services.tag, InputType.TEXT, false),
      textField('externalKeyClaim', images.services.tag, InputType.TEXT, false),
    ],
  };
  return [
    { title: 'integrations.identityProviders.data.name', img: images.sidebar.identityProvider, valueToChange: 'name',
      inputType: InputType.TEXT, displayValue: draft.name, required: true },
    { title: 'integrations.identityProviders.data.provider', img: images.services.tag, valueToChange: 'provider',
      inputType: InputType.SELECT_SEARCH, translateValue: true, required: true, disabled: existing,
      displayValue: draft.provider ? `integrations.identityProviders.providers.${draft.provider}` : '',
      editValue: draft.provider,
      values: selectOptions(Object.values(IdentityProviderType), 'integrations.identityProviders.providers.') },
    ...userFields,
    ...(draft.provider ? (configurationFields[draft.provider] ?? []) : []),
  ].map((definition) => buildDetailFormField(definition, edit));
}

export function getSaeIntegrationForm(draft: ISaeIntegrationDraft, edit: boolean): IDetailFormField[] {
  return [
    { title: 'integrations.saeIntegrations.data.name', img: images.services.routes.sae, valueToChange: 'name',
      inputType: InputType.TEXT, displayValue: draft.name, required: true },
    { title: 'integrations.saeIntegrations.data.type', img: images.services.tag, valueToChange: 'type',
      inputType: InputType.SELECT_SEARCH, translateValue: true, required: true, editValue: draft.type,
      displayValue: draft.type ? `integrations.saeIntegrations.types.${draft.type}` : '',
      values: selectOptions(Object.values(SaeType), 'integrations.saeIntegrations.types.') },
    { title: 'integrations.saeIntegrations.data.positionsEndpoint', img: images.link,
      valueToChange: 'positionsEndpoint', inputType: InputType.LONG_TEXT, displayValue: draft.positionsEndpoint },
    { title: 'integrations.saeIntegrations.data.routesEndpoint', img: images.link, valueToChange: 'routesEndpoint',
      inputType: InputType.LONG_TEXT, displayValue: draft.routesEndpoint },
  ].map((definition) => buildDetailFormField(definition, edit));
}
